import React, { useMemo, useState } from 'react'
import Tabs, { TabType } from 'components/Tabs'
import getWeekDates from 'utils/dates'
import Map, { MapCenterType } from 'components/pages/MarketPage/TrucksMap/Map'
import { MapIcon } from '@heroicons/react/outline'
import startOfDayUtc from 'utils/startOfDayUtc'
import addDays from 'date-fns/addDays'
import { useQuery } from '@apollo/client'
import {
  mapLocationGroupsQuery,
  mapLocationGroupsQueryVariables
} from 'components/pages/MarketPage/TrucksMap/types/mapLocationGroupsQuery'
import QUERY from 'components/pages/MarketPage/TrucksMap/mapLocationGroupsQuery'
import toApiDate from 'utils/toApiDate'
import Loading from 'components/Loading'
import PageError from 'components/pages/PageError'
import cn from 'classnames'
import ButtonLink from 'components/ui/ButtonLink'
import { marketQuery_marketBySlug } from 'components/pages/MarketPage/types/marketQuery'
import { MarketsNearMeQuery } from 'components/pages/TrucksNearbyPage/types/marketsNearMe'

type Props = {
  defaultCenter: MapCenterType
  market:
    | marketQuery_marketBySlug
    | NonNullable<MarketsNearMeQuery>['nearbyMarkets']['records'][0]
  showTitle?: boolean
  showTabs?: boolean
  showInNewPage?: boolean
}

const MAP_HEIGHT = 600

const TrucksMap = ({
  defaultCenter,
  market,
  showTitle = true,
  showTabs = true,
  showInNewPage = false
}: Props) => {
  const thisWeekDates = useMemo(() => getWeekDates(), [])
  const [currentTab, setCurrentTab] = useState<TabType>(thisWeekDates[0])

  const fromDate = currentTab.value
    ? startOfDayUtc(currentTab.value as Date)
    : startOfDayUtc(new Date())
  const toDate = currentTab.value
    ? addDays(fromDate, 1)
    : addDays(startOfDayUtc(new Date()), 7)
  const { error, data, previousData } = useQuery<
    mapLocationGroupsQuery,
    mapLocationGroupsQueryVariables
  >(QUERY, {
    fetchPolicy: 'cache-first',
    variables: {
      fromDate: toApiDate(fromDate),
      toDate: toApiDate(toDate),
      marketId: market.id
    }
  })
  const loading = (!data || !data.mapLocationGroups) && !previousData && !error
  if (loading) {
    return <Loading />
  }
  if (error) {
    return <PageError>{error.message}</PageError>
  }
  if (data?.mapLocationGroups.length === 0) {
    return null
  }

  const groups = data?.mapLocationGroups || previousData?.mapLocationGroups

  return (
    <div
      className={cn('mx-auto', {
        'md:py-14 py-6': showTitle
      })}
    >
      {showTitle && (
        <div className="flex flex-col items-center md:pb-10 pb-6">
          <div className="p-3 rounded-full bg-gray-700">
            <MapIcon className="md:w-7 md:h-7 h-6 w-6 text-gray-50" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold leading-5 text-gray-700 mt-5 text-center px-4">
            Find trucks on the map to order online
          </h2>
        </div>
      )}

      <div className="rounded-xl overflow-hidden shadow">
        {showTabs && (
          <Tabs
            additionalButton={
              <ButtonLink
                color="primary"
                href={market.customerTrucksMapPath}
                prefetch={false}
                variant="anchor"
              >
                <span className="font-bold">Full Map</span>
              </ButtonLink>
            }
            color="primary"
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
            tabs={thisWeekDates}
            variant="outlined"
            fullWidth
          />
        )}
        <div
          className="md:rounded-b-lg relative"
          style={{ height: MAP_HEIGHT }}
        >
          <Map
            defaultCenter={defaultCenter}
            groups={groups!}
            height={MAP_HEIGHT}
            showInNewPage={showInNewPage}
          />
        </div>
      </div>
    </div>
  )
}

export default TrucksMap
